// 工具函数集合
import type { Product, ProductAttribute, SkuKVItem } from '@/types';
import i18n from '../i18n';

/**
 * 格式化价格（统一为日币 JPY¥，按当前语言 locale 做千分位）
 */
export const formatPrice = (price: number, locale?: string): string => {
  const loc = locale ?? i18n.language ?? 'zh';
  const formatted = Math.round(price).toLocaleString(loc === 'ja' ? 'ja-JP' : loc === 'zh' ? 'zh-CN' : loc, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return `JPY¥${formatted}`;
};

/** 将 skuKV 格式化为「属性:值, 属性:值」用于展示；空则返回「—」 */
export function formatSkuKV(
  skuKV: Array<
    SkuKVItem | {
      propertyName?: string;
      propertyValue?: string;
      key?: string;
      value?: string;
    }
  >
): string {
  if (!skuKV?.length) return '—';
  const normalized = skuKV
    .map((x) => {
      const raw = x as {
        propertyName?: string;
        propertyValue?: string;
        key?: string;
        value?: string;
      };
      return {
        propertyName: raw.propertyName ?? raw.key ?? '',
        propertyValue: raw.propertyValue ?? raw.value ?? '',
      };
    })
    .filter((x) => x.propertyName && x.propertyValue);
  if (!normalized.length) return '—';
  return normalized.map((x) => `${x.propertyName}:${x.propertyValue}`).join('，');
}

/** 判断两个 skuKV 是否表示同一规格组合（顺序可不同） */
export function skuKVEqual(a: SkuKVItem[], b: SkuKVItem[]): boolean {
  if (a.length !== b.length) return false;
  const toKey = (x: SkuKVItem) => `${x.propertyName}\0${x.propertyValue}`;
  const setA = new Set(a.map(toKey));
  return b.every((x) => setA.has(toKey(x)));
}

/**
 * 根据属性定义计算所有规格组合（笛卡尔积）。
 * 空属性名、空取值会忽略；无有效属性时返回 [[]] 表示一个「无规格」组合。
 */
export function computeCombinations(attrs: ProductAttribute[]): SkuKVItem[][] {
  const list = (attrs || [])
    .filter((a) => (a.name || '').trim() !== '')
    .map((a) => {
      const uniqueValues = Array.from(
        new Set((a.values || []).map((v) => (v || '').trim()).filter((v) => v !== ''))
      );
      return {
        name: a.name.trim(),
        values: uniqueValues,
      };
    })
    .filter((a) => a.values.length > 0);

  if (list.length === 0) return [[]];

  function rec(i: number): SkuKVItem[][] {
    if (i >= list.length) return [[]];
    const cur = list[i];
    const rest = rec(i + 1);
    const out: SkuKVItem[][] = [];
    for (const v of cur.values) {
      for (const r of rest) {
        out.push([{ propertyName: cur.name, propertyValue: v }, ...r]);
      }
    }
    return out;
  }
  return rec(0);
}

/** 取商品所有 SKU 中的最低售价，无 variants 时返回 undefined */
export function getMinVariantPrice(p: Product): number | undefined {
  const vs = p.variants;
  if (!vs?.length) return undefined;
  return Math.min(...vs.map((v) => v.price));
}

/** 取商品所有 SKU 中的最高售价，无 variants 时返回 undefined */
export function getMaxVariantPrice(p: Product): number | undefined {
  const vs = p.variants;
  if (!vs?.length) return undefined;
  return Math.max(...vs.map((v) => v.price));
}

/** 用于列表展示：是否有任 SKU 存在原价且原价高于售价（折扣） */
export function hasAnyVariantOnSale(p: Product): boolean {
  return (p.variants ?? []).some((v) => (v.compareAtPrice ?? 0) > v.price);
}

/** 取「最低价」那个 SKU 的原价，用于列表划线价展示；若该 SKU 无原价则返回 undefined */
export function getCompareAtPriceForMinVariant(p: Product): number | undefined {
  const vs = p.variants;
  if (!vs?.length) return undefined;
  const minV = vs.reduce((a, b) => (a.price <= b.price ? a : b));
  return minV.compareAtPrice && minV.compareAtPrice > minV.price ? minV.compareAtPrice : undefined;
}

/** 商品总库存 = 各 SKU 库存之和；无 variants 时回退为 product.inventory */
export function getProductInventory(p: Product): number {
  const vs = p.variants;
  if (vs?.length) return vs.reduce((s, v) => s + (v.inventory || 0), 0);
  return p.inventory ?? 0;
}

/**
 * 格式化日期（按当前语言 locale）
 */
export const formatDate = (date: string | Date, locale?: string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  const loc = locale ?? i18n.language ?? 'zh';
  const resolved = loc === 'ja' ? 'ja-JP' : loc === 'zh' ? 'zh-CN' : loc;
  return d.toLocaleDateString(resolved, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * 格式化日期时间（按当前语言 locale）
 */
export const formatDateTime = (date: string | Date, locale?: string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  const loc = locale ?? i18n.language ?? 'zh';
  const resolved = loc === 'ja' ? 'ja-JP' : loc === 'zh' ? 'zh-CN' : loc;
  return d.toLocaleString(resolved);
};

/**
 * 防抖函数
 */
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * 节流函数
 */
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let lastTime = 0;
  return (...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastTime >= wait) {
      lastTime = now;
      func(...args);
    }
  };
};

/**
 * 生成唯一ID
 */
export const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/** 开发环境将 static.dajitui.fun 的 OSS 图片 URL 改写为本地代理路径，绕过防盗链 */
export function getOssProxiedUrl(url: string): string {
  if (!url) return '';
  const ossOrigin = 'https://static.dajitui.fun';
  if (import.meta.env.DEV && url.startsWith(ossOrigin)) {
    return '/oss-proxy' + url.slice(ossOrigin.length);
  }
  return url;
}

/**
 * 使用 RSA 公钥加密密码后发送给后端（与后端 decryptRSA + BCrypt 约定一致）。
 * 公钥由后端接口 POST /c/rsa/publickey 返回（BaseResult 包装的字符串），与 RTK / 上传服务同源 baseUrl（见 getApiBaseUrl）。
 * 支持完整 PEM 或纯 Base64 SPKI；使用 node-forge 实现 RSAES-PKCS1_V1_5。
 *
 * @param password 原始密码
 * @returns Promise<string> Base64 编码的 RSA 密文
 */
import forge from 'node-forge';
import { getApiBaseUrl } from '@utils/apiBaseUrl';
import { applyRefererHeader, withRequestReferrer } from '@utils/requestReferrer';

const PEM_HEADER = '-----BEGIN PUBLIC KEY-----';
const PEM_FOOTER = '-----END PUBLIC KEY-----';

function getPemFromRaw(raw: string): string {
  const trimmed = raw.replace(/\s/g, '');
  if (trimmed.includes('BEGINPUBLICKEY') || trimmed.includes('BEGIN PUBLIC KEY')) {
    return raw.trim();
  }
  return `${PEM_HEADER}\n${trimmed}\n${PEM_FOOTER}`;
}

let cachedForgePublicKey: forge.pki.PublicKey | null = null;
let publicKeyLoadPromise: Promise<forge.pki.PublicKey> | null = null;

/**
 * 调用 NoAuthController /c/rsa/publickey，body 为 LoginRequest（后端仅需 JSON 体占位）。
 */
async function fetchRsaPublicKeyFromServer(): Promise<string> {
  const headers = applyRefererHeader(new Headers({ 'Content-Type': 'application/json' }));
  const res = await fetch(`${getApiBaseUrl()}/c/rsa/publickey`, withRequestReferrer({
    method: 'POST',
    headers,
    body: JSON.stringify({ email: '', password: '' }),
    credentials: 'include',
  }));
  if (!res.ok) {
    throw new Error(`获取 RSA 公钥失败: HTTP ${res.status}`);
  }
  const text = await res.text();
  let json: { success?: boolean; errorMessage?: string; data?: string };
  try {
    json = JSON.parse(text) as typeof json;
  } catch {
    throw new Error('获取 RSA 公钥失败: 响应不是合法 JSON');
  }
  if (json.success === false) {
    throw new Error(json.errorMessage || '获取 RSA 公钥失败');
  }
  const data = json.data;
  if (!data || typeof data !== 'string' || !data.trim()) {
    throw new Error('RSA 公钥为空');
  }
  return data.trim();
}

async function getForgePublicKey(): Promise<forge.pki.PublicKey> {
  if (cachedForgePublicKey) return cachedForgePublicKey;
  if (!publicKeyLoadPromise) {
    publicKeyLoadPromise = (async () => {
      const raw = await fetchRsaPublicKeyFromServer();
      const pem = getPemFromRaw(raw);
      try {
        return forge.pki.publicKeyFromPem(pem);
      } catch {
        throw new Error('RSA 公钥格式无效');
      }
    })()
      .then((key) => {
        cachedForgePublicKey = key;
        return key;
      })
      .finally(() => {
        publicKeyLoadPromise = null;
      });
  }
  return publicKeyLoadPromise;
}

export const encryptPasswordRSA = async (password: string): Promise<string> => {
  const publicKey = await getForgePublicKey();
  const bytes = forge.util.encodeUtf8(password);
  const encrypted = (publicKey as { encrypt(data: string, scheme?: string): string }).encrypt(bytes, 'RSAES-PKCS1-V1_5');
  return forge.util.encode64(encrypted);
};
